<template>
	<div id="app" :class="[{ 'kiosk': this.isKiosk }]">
		<router-view />
	</div>
</template>

<script>
	import localizationData from './data/LocalizedStrings.json';

	export default {
		data: function() {
			return {
				sharedStore: window.store
			}
		},
		mounted: function() {
			// run setup functions, retrieve data
			this.getScreenLocation();
			this.fetchMapLabelData();
			this.fetchTodaysEvents();
			// this.fetchPopupData();
			this.fetchDirectoryData();

			// setup content preview handler
			window.addEventListener('message', this.handlePreviewData, false);
		},
		destroyed() {
			window.removeEventListener('message', this.handlePreviewData);
		},
		computed: {
			isKiosk: function() {
				return this.sharedStore.state.screen !== "external";
			}
		},
		methods: {
			fetchDirectoryData: function() {
				let fetchUrl = process.env.VUE_APP_COCKPIT_URL + '/api/collections/get/DirectoryTab?populate=-1';

				fetch(fetchUrl, {
					headers: { 'Cockpit-Token': process.env.VUE_APP_COCKPIT_KEY },
					cache: "no-store"
				})
					.then(collection => collection.json())
					.then(collection => {
						// process api data into a sane format
						let formattedCollection = this.formatDirectoryData(collection);

						// map to various useful arrays
						let categoryArr = formattedCollection.flatMap(o => {
							return o.categories;
						});
						let locationArr = categoryArr.flatMap(o => {
							return o.locations;
						});
						let locationsAndSubcategories = locationArr.concat(locationArr.flatMap(o => {
							return o.subcategories;
						}));

						// update directory data
						this.sharedStore.setDirectoryData(formattedCollection);
						this.sharedStore.setLocationData(locationsAndSubcategories);

						// update image paths for each location
						this.sharedStore.state.locationData.forEach((item, index) => {
							this.fetchResizedImages(item.imagesRaw, index);
						});	
					});
			},
			formatDirectoryData: function(collection) {
				let formatted = [];
				// process directory tabs
				collection.entries.forEach(entry => {
					let categories = this.processLocationCategories(entry.LocationCategory, entry.Name);
					// get the base info for each directory category
					let nextEntry = {
						name: entry.Name,
						id: entry.TabID,
						categories: categories
					};
					// map localization strings to each entry as well
					let localizedNames = this.getLocalizedEntries(entry, "name", "Name");
					Object.assign(nextEntry, localizedNames);
					
					formatted.push(nextEntry);
				});

				return formatted;
			},
			processLocationCategories: function(categories, directory) {
				let formatted = [];
				if (!Array.isArray(categories) || !categories.length) {
					return [];
				}
				categories.forEach(entry => {
					let locations = this.processLocations(entry.Locations, directory, entry.Name);
					let nextEntry = {
						name: entry.Name,
						directoryTab: directory,
						locations: locations
					};
					// map localization strings to each entry as well
					let localizedNames = this.getLocalizedEntries(entry, "name", "Name");
					Object.assign(nextEntry, localizedNames);

					formatted.push(nextEntry);
				});
				return formatted;
			},
			processLocations: function(locations, directory, category) {
				let formatted = [];
				if (!Array.isArray(locations) || !locations.length) {
					return [];
				}

				locations.forEach(entry => {
					let code = "";
					let subcategories = [];

					if (typeof entry.MapCode !== "undefined") {
						if (entry.MapCode.MapID) {
							code = entry.MapCode.MapID;
						} else if (entry.MapCode.display) {
							code = entry.MapCode.display;
						} else {
							code = "";
						}
					}

					let hidden = false;
					if (typeof entry.Hidden === "boolean") {
						hidden = entry.Hidden;
					}

					let localizedSubcategories = {};

					if (entry.Subcategories && entry.Subcategories.length) {
						// still use base data, primarily for images
						subcategories = this.parseSubcategoryList(entry.Subcategories, entry.Name);
						subcategories = this.processSubcategoryList(subcategories, entry, directory, category);

						// process and localize subcategories for each language
						localizedSubcategories = this.getLocalizedEntries(entry, "subcategories", "Subcategories");

						for (let locSub in localizedSubcategories) {
							localizedSubcategories[locSub] = this.parseLocalizedSubcategory(localizedSubcategories[locSub]);
						}
					}
				
					let nextEntry = {
						id: entry._id,
						name: entry.Name,
						description: entry.Description,
						imagesRaw: [],
						images: [],
						code: code,
						hidden: hidden,
						subcategories: subcategories,
						directoryTab: directory,
						directoryCategory: category
					};

					// map localization strings to each entry as well
					let localizedNames = this.getLocalizedEntries(entry, "name", "Name");
					let localizedDescriptions = this.getLocalizedEntries(entry, "description", "Description");
					Object.assign(nextEntry, localizedNames);
					Object.assign(nextEntry, localizedDescriptions);
					Object.assign(nextEntry, localizedSubcategories);

					nextEntry.imagesRaw = this.parseImageList(entry.Images);

					formatted.push(nextEntry);
				});
				return formatted;
			},
			parseImageList: function(list) {
				let imagePaths = [];
				if (typeof list !== "undefined" && list.length) {
					imagePaths = list.map(obj => {
						return obj.path;
					});
				}
				return imagePaths;
			},
			parseSubcategoryList: function(list, parent) {
				let subList = [];
				if (typeof list !== "undefined" && list !== null && list.length > 0) {
					list.forEach((sub, index) => {
						let newSub = {
							id: "",
							name: sub.value.sc_name,
							description: sub.value.Description,
							imagesRaw: [],
							images: [],
							parent: parent,
							subIndex: index,
							directoryTab: "",
							directoryCategory: ""
						};

						newSub.imagesRaw = this.parseImageList(sub.value.Images);

						subList.push(newSub);
					});
				}
				return subList;
			},
			parseLocalizedSubcategory: function(list) {
				// for localized subcategories, we only need to retrieve the localized strings
				let subList = [];
				if (typeof list !== "undefined" && list !== null && list.length > 0) {
					list.forEach((sub) => {
						let newSub = {
							name: sub.value.sc_name,
							description: sub.value.Description
						};

						subList.push(newSub);
					});
				}
				return subList;
			},
			processSubcategoryList: function(subcategories, entry, directory, category) {
				subcategories.forEach(sub => {
					// update with appropriate directory data
					sub.id = entry._id + "_" + sub.subIndex;
					sub.directoryTab = directory;
					sub.category = category;
				});
				return subcategories;
			},
			fetchResizedImages: function(imagesRaw, index) {
				if (this.sharedStore.state.activeImageFetchRequests.indexOf(index) > -1) {
					// already fetching images, ignore
					console.warn("Already fetching image URLs, ignoring");
					return;
				} else {
					this.sharedStore.addImageFetchFlag(index);
				}
				imagesRaw.forEach(path => {
					let fetchUrl = process.env.VUE_APP_COCKPIT_URL + "/api/cockpit/image?" + "src=" + path + "&w=500";

					fetch(fetchUrl, {
						headers: { 'Cockpit-Token': process.env.VUE_APP_COCKPIT_KEY }
					})
						.then(asset => asset.text())
						.then(asset => {
							this.sharedStore.addImageForLocation(index, asset);
						});
				});	
			},
			fetchPopupData: function() {
				// get singleton info for any temporary messaging to display
				let fetchUrl = process.env.VUE_APP_COCKPIT_URL + '/api/singletons/get/PopUpMessaging';

				fetch(fetchUrl, {
					headers: { 'Cockpit-Token': process.env.VUE_APP_COCKPIT_KEY },
					cache: "no-store"
				})
					.then(singleton => singleton.json())
					.then(singleton => {
						this.processPopupData(singleton);
					});
			},
			fetchTodaysEvents: function() {
				// get singleton info for today's events
				let fetchUrl = process.env.VUE_APP_COCKPIT_URL + '/api/singletons/get/TodaysEvents?populate=-1';

				fetch(fetchUrl, {
					headers: { 'Cockpit-Token': process.env.VUE_APP_COCKPIT_KEY },
					cache: "no-store"
				})
					.then(singleton => singleton.json())
					.then(singleton => {
						this.processTodaysEventsData(singleton);
					});
			},
			processPopupData: function(data) {
				// TODO Localize
				let newData = {
					title: data.Title,
					description: data.Description,
					showPopup: data.ShowPopup,
					autoOpen: data.AutoOpen,
					listItems: []
				};
 
				let localizedTitles = this.getLocalizedEntries(data, "title", "Title");
				let localizedDescriptions = this.getLocalizedEntries(data, "description", "Description");
				Object.assign(newData, localizedTitles);
				Object.assign(newData, localizedDescriptions);

				// process and localize entries for each language
				// get base localized strings
				let localizedListItems = this.getLocalizedEntries(data, "list", "List");

				data.List.forEach((item, index) => {
					if (item && item.value) {
						let newItem = {
							leftCol: item.value.LeftColumn || "",
							rightCol: item.value.RightColumn || ""
						};
 
						// process and add localized entries
						for (let key in localizedListItems) {
							let lang = key.replace("list_", "");
							let locItem = localizedListItems[key][index].value;
							newItem["leftCol_" + lang] = locItem.LeftColumn || "";
							newItem["rightCol_" + lang] = locItem.RightColumn || "";
						}
 
						newData.listItems.push(newItem);
					}
				});

				this.sharedStore.setPopupData(newData);
			},
			processTodaysEventsData: function(data) {
				// TODO Localize
				let newData = {
					title: data.Title || "Today's Events",
					showTodaysEvents: data.ShowTodaysEvents,
					showDate: data.ShowDate,
					autoOpen: data.AutoOpen,
					listItems: []
				};

				let localizedTitles = this.getLocalizedEntries(data, "title", "Title");
				Object.assign(newData, localizedTitles);
				// process and localize entries for each language
				// get base localized strings
				let localizedListItems = this.getLocalizedEntries(data, "eventList", "EventList");

				data.EventList.forEach((item, index) => {
					const eventStarted = (item.value.Start_Date) ? new Date(item.value.Start_Date) < new Date() : true;
					let endDate = null;
					if (item.value.End_Date) {
						let dateRegex = /(\d{4}).(0[1-9]|1[0-2]).(0[1-9]|[1-2][0-9]|3[0-1])/g;
						let parsedDate = dateRegex.exec(item.value.End_Date);
						if (parsedDate.length === 4) {
							let formattedDate = parsedDate[1] + "/" + parsedDate[2] + "/" + parsedDate[3];
							endDate = new Date(formattedDate);
							endDate.setUTCHours(23,59,59,999);
						}
					}
					
					const eventPassed = (endDate) ? endDate < new Date() : false;

					if (eventStarted && !eventPassed) {
						let newItem = {
							eventTitle: item.value.Event_Title || "",
							description: item.value.Description || "",
							eventType: item.value.Event_Category || "",
							eventLink: item.value.Event_Link || "https://buytickets.atlantahistorycenter.com/webstore/shop/viewItems.aspx?cg=AHCCG&c=FPCAT",
							startTime: item.value.Start_Time || "",
							endTime: item.value.End_Time || "",
							location: item.value.Location || null,
							image: null
						}

						if (item.value.Alternate_Location_Name) {
							newItem.locationName = item.value.Alternate_Location_Name; 
						} else {
							newItem.locationName = (newItem.location) ? newItem.location.Name : "";
						}

						if (item.value.Image) {
							let fetchUrl = process.env.VUE_APP_COCKPIT_URL + "/api/cockpit/image?" + "src=" + item.value.Image.path + "&w=500";

							fetch(fetchUrl, {
								headers: { 'Cockpit-Token': process.env.VUE_APP_COCKPIT_KEY }
							})
								.then(asset => asset.text())
								.then(asset => {
									newItem.image = asset
								});
						}

						//process and add localized entries
						for (let key in localizedListItems) {
							let lang = key.replace("eventList_", "");
							if (localizedListItems[key][index]) {
								let locItem = localizedListItems[key][index].value;
								newItem["eventTitle_" + lang] = locItem.Event_Title  || "";
								newItem["description_" + lang] = locItem.Description || "";
								newItem["eventType_" + lang] = locItem.Event_Category || "";
								newItem["location_" + lang] = (locItem.Location) ? locItem.Location.Name : "";
                
								if (locItem.Alternate_Location_Name) {
									newItem["locationName_" + lang] = locItem.Alternate_Location_Name;
								} else {
									newItem["locationName_" + lang] = newItem["location_" + lang];
								}
							}
						}

						newData.listItems.push(newItem);
					}
				});

				this.sharedStore.setTodaysEvents(newData);
			},
			fetchMapLabelData: function() {
				// get singleton info for any temporary messaging to display
				let fetchUrl = process.env.VUE_APP_COCKPIT_URL + '/api/collections/get/MapLabels?populate=-1';

				fetch(fetchUrl, {
					headers: { 'Cockpit-Token': process.env.VUE_APP_COCKPIT_KEY },
					cache: "no-store"
				})
					.then(collection => collection.json())
					.then(collection => {
						let mapData = [];

						collection.entries.forEach((area) => {
							let areaData = this.processMapLabelData(area);
							mapData = mapData.concat(areaData);
						});
						
						this.sharedStore.setMapLabelData(mapData);
					});
			},
			processMapLabelData: function(area) {
				let newData = [];

				// Get localized label array, since this is a repeater
				let localizedLabels = this.getLocalizedEntries(area, "labels", "Labels");

				area.Labels.forEach((label, index) => {
					// convert strings to float arrays
					let pos = [50, 50];
					if (typeof label.value.Position === "string" && label.value.Position !== "") {
						pos = this.convertStringToCoordinates(label.value.Position, [50, 50]);
					}
					let anchor = [50, 50];
					if (typeof label.value.Anchor === "string" && label.value.Anchor !== "") {
						anchor = this.convertStringToCoordinates(label.value.Anchor, [50, 50]);
					}
					let range = [0, 3];
					if (typeof label.value.ZoomRange === "string" && label.value.ZoomRange !== "") {
						range = this.convertStringToCoordinates(label.value.ZoomRange, [0, 3]);
					}
					let hidden = false;
					if (typeof label.value.Hidden === "boolean") {
						hidden = label.value.Hidden;
					}
					let code = null;

					if (typeof label.value.MapCode === "object" && label.value.MapCode !== null) {
						if (label.value.MapCode.MapID) {
							code = label.value.MapCode.MapID;
						} else if (label.value.MapCode.display) {
							code = label.value.MapCode.display;
						} else {
							code = null;
						}
					}

					let newLabel = {
						id: area._id + "_" + index,
						area: area.MapLabelArea.toLowerCase(),
						text: label.value.Text || "",
						size: label.value.Size || 16,
						bold: label.value.Bold,
						color: label.value.Color || "#000000",
						outline: label.value.Outline || "#FFFFFF",
						position: {x: pos[0], y: pos[1]},
						anchor: {x: anchor[0], y: anchor[1]},
						range: {min: range[0], max: range[1]},
						code: code,
						hidden: hidden,
						scaleAdjust: 0
					}

					// get localized label entries and add to the main label data
					for (let key in localizedLabels) {
						let lang = key.replace("labels_", "");
						let localizedEntry = localizedLabels[key][index].value;
						// Only localize the text and size values for each entry
						newLabel["text_" + lang] = localizedEntry.Text;
						newLabel["size_" + lang] = localizedEntry.Size;
					}

					newData.push(newLabel);
				});

				return newData;
			},
			convertArrayToObject: (array, key) => {
				const initialValue = {};
				return array.reduce((obj, item) => {
					return {
						...obj,
						[item[key]]: item,
					};
				}, initialValue);
			},
			convertStringToCoordinates: function(str, defaultValue) {
				// Assume an input string of e.g. "50,50"
				let arr = str.trim().split(',');
				let newArr = [parseFloat(arr[0]), parseFloat(arr[1])];
				// If we cannot parse two coordinates, default to 50,50 position
				if (typeof newArr[0] !== "number" || typeof newArr[1] !== "number") {
					newArr = defaultValue;
				}
				return newArr;
			},
			getScreenLocation: function() {
				let screen = this.getURLParameterByName("screen");

				if (screen !== null) {
					this.sharedStore.setScreenLocation(screen);
				}
			},
			getURLParameterByName(name, escapeSpaces = false){
				if(escapeSpaces === null) escapeSpaces = true;
				var match = new RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
				return (escapeSpaces) ? match && decodeURIComponent(match[1].replace(/\/+/g, '')) : match && decodeURIComponent(match[1]);
			},
			handlePreviewData: function(event) {
				// Re-add event log when developing new section previews
				if (event.data.collection && event.data.collection === "Locations") {
					// update a specific location
					this.handleLocationPreview(event.data.entry);
				} else if (event.data.collection && event.data.collection === "MapLabels") {
					// update a specific location
					this.handleLabelPreview(event.data.entry);
				} else {
					console.log("Cannot preview this event type:", event.data.collection);
				}
			},
			handleLocationPreview: function(entry) {
				let locationIndex = this.sharedStore.getLocationIndexById(entry._id);
				if (locationIndex <= -1) {
					// no existing location, so we don't know anything about the directory for this item
					console.log("Cannot find match for ", entry);
					return;
				}

				// just for main location - this does not handle subcategories yet
				let existingLocation = this.sharedStore.state.locationData[locationIndex];

				let directory = existingLocation.directoryTab;
				let category = existingLocation.directoryCategory;

				let processed = this.processLocations([entry], directory, category);
				
				let processedFlat = processed.concat(processed.flatMap(o => {
					return o.subcategories;
				}));

				// loop through everything, in case we have subcategories which were flatmapped
				processedFlat.forEach((item) => {
					let imagesNeedUpdate = false;
					let itemIndex = this.sharedStore.getLocationIndexById(item.id);
					let itemMatch = this.sharedStore.state.locationData[itemIndex];

					if (JSON.stringify(item.imagesRaw) === JSON.stringify(itemMatch.imagesRaw) && itemMatch.images.length === itemMatch.imagesRaw.length) {
						// same image list
						item.images = itemMatch.images;
					} else {
						imagesNeedUpdate = true;
					}

					this.sharedStore.updateLocationData(itemIndex, item);

					// if images changed at all, update image paths
					if (imagesNeedUpdate) {
						this.fetchResizedImages(item.imagesRaw, itemIndex);	
					}
				});
			},
			handleLabelPreview: function(entry) {
				let entryData = this.processMapLabelData(entry);
				this.sharedStore.updateMapLabelData(entryData);
			},
			getLocalizedEntries: function(entry, keyPrefix, valPrefix) {
				let localizedValues = {};
				localizationData.languages.forEach((lang) => {
					let langCode = keyPrefix + "_" + lang.code;
					let langString = valPrefix;
					if (lang.string !== "default") {
						langString = valPrefix + "_" + lang.string;
					}
					localizedValues[langCode] = (entry[langString] && entry[langString].length)  ? entry[langString] : entry[valPrefix];
				});

				return localizedValues;
			}
		}
	}
</script>

<style lang="scss">
	* {
		box-sizing: border-box;
	}

	html, body {
		margin: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		font-family: 'Gotham Narrow', 'Gotham Narrow A', 'Gotham Narrow B', 'Gotham', Helvetica, Arial, sans-serif;
		touch-action: none;
		// fill with map green background in cases where map aspect is off
		background: rgb(14, 121, 97);
	}
	#app {
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
		text-align: center;
		color: #2c3e50;
		height: 100%;
	}

	#nav {
		padding: 30px;

		a {
			font-weight: bold;
			color: #2c3e50;

			&.router-link-exact-active {
				color: #42b983;
			}
		}
	}

	li {
		list-style: none;
		margin: 0;
	}

	.kiosk {
		font-size: 2em;
	}
</style>
